<template>
  <div
    class="border rounded-2xl border-slate-200 py-2 px-3 flex items-center gap-4"
  >

    <FilePreview
      ref="fileAttachmentPreview"
      :attachment="modelValue"
    />

    <div
      class="space-y-1 w-full flex-1 z-0"
    >
      <div
        class="flex flex-col items-start justify-start gap-x-4 gap-y-2 flex-1 py-1 w-full md:flex-row md:items-center md:justify-between max-w-full"
      >
        <div
          class="relative group w-full flex-1"
          :class="isViewOnly && 'cursor-pointer-important'"
          @click="handleInputClick"
        >
          <input
            placeholder="Add a descriptive name"
            class="py-2.5 w-full truncate rounded-lg border focus:border-slate-200 focus:ring-2 ring-offset-2 focus:outline-none pl-3 pr-10 focus:pr-3"
            :value="modelValue.current_name"
            :readonly="isViewOnly"
            :class="isViewOnly ? 'border-transparent ring-transparent cursor-pointer-important bg-transparent' : 'border-slate-50 ring-bdBlue-200'"
            @input="(e) => updateModelValue('current_name', e.target.value)"
          />
          <FontAwesomeIcon
            v-if="!isViewOnly"
            :icon="faPencil"
            class="absolute top-1/2 -translate-y-1/2 right-4 text-slate-400 text-sm bg-white rounded-lg pointer-events-none group-focus-within:hidden"
            aria-hidden="true"
          />
        </div>

        <div class="gap-2 flex items-center max-w-fit">

          <HoverTooltip
            v-if="orderOptions && !isViewOnly && total > 1"
            tip="Change Order"
            placement="top"
          >
            <FormSelectDropdown
              class="w-14 min-w-14 -mr-0.5"
              should-parse-int
              placeholder="Select Order"
              padding-classes="py-2.5 pl-2 pr-5 text-center"
              :model-value="modelValue?.order || 0"
              :options="orderOptions"
              falsy-is-valid
              @update:modelValue="switchOrder"
            />
          </HoverTooltip>

          <HoverTooltip
            v-if="isPreViewable"
            tip="Preview File"
            placement="top"
          >
            <button
              type="button"
              class="h-11 w-11 min-w-11 flex items-center justify-center bg-slate-100 text-slate-600 hover:bg-slate-200 rounded-xl focus:outline-none focus:ring ring-offset-2 ring-bdBlue-200"
              aria-label="Preview Image"
              @click="previewFile"
            >
              <FontAwesomeIcon
                :icon="faEye"
              />
            </button>
          </HoverTooltip>

<!--          <HoverTooltip-->
<!--            v-if="isCroppableImage && !isViewOnly"-->
<!--            tip="Crop Image"-->
<!--            placement="top"-->
<!--          >-->
<!--            <button-->
<!--              type="button"-->
<!--              class="h-11 w-11 min-w-11 flex items-center justify-center bg-slate-100 text-slate-600 hover:bg-slate-200 rounded-xl focus:outline-none focus:ring ring-offset-2 ring-bdBlue-200"-->
<!--              aria-label="Crop Image"-->
<!--              @click="showCropper = true"-->
<!--            >-->
<!--              <FontAwesomeIcon-->
<!--                :icon="faCropSimple"-->
<!--              />-->
<!--            </button>-->
<!--          </HoverTooltip>-->

          <UtilityDeleteIcon
            v-if="!isViewOnly"
            aria-label="Detach File"
            @delete="() => emits('delete')"
          />

          <HoverTooltip
            v-if="isViewOnly"
            tip="Download File"
            placement="top"
          >
            <button
              type="button"
              class="h-11 w-11 min-w-11 flex items-center justify-center bg-slate-100 text-slate-600 hover:bg-slate-200 rounded-xl focus:outline-none focus:ring ring-offset-2 ring-bdBlue-200"
              aria-label="Download File"
              @click="handleDownload"
            >
              <FontAwesomeIcon
                :icon="faDownload"
              />
            </button>
          </HoverTooltip>

        </div>
      </div>

      <FormSwitch
        v-if="props.showFileLibrary && isNewUpload && !isViewOnly"
        :model-value="!!modelValue?.add_to_library"
        class="text-xs font-medium text-slate-400"
        label="Add to library for future use"
        @update:model-value="updateModelValue('add_to_library', $event)"
      />
    </div>

    <UtilityCropper
      v-if="isCroppableImage && !isViewOnly"
      v-model:open="showCropper"
      :file="modelValue.file"
      :image="modelValue.original_path"
      disable-cancel
      @update:file="updateModelValue('file', $event)"
      @update:image="updateModelValue('original_path', $event)"
    />
  </div>
</template>

<script setup>
import FormSwitch from "./FormSwitch.vue";
import FilePreview from "./FilePreview.vue";
import FormSelectDropdown from "./FormSelectDropdown.vue";
import HoverTooltip from "./HoverTooltip.vue";
import UtilityCropper from "./UtilityCropper.vue";
import UtilityDeleteIcon from "./UtilityDeleteIcon.vue";
import {computed, ref} from "vue";
import {faEye, faPencil, faDownload} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import {mimeToExtension} from "../constants/FileLibrary.js";

const emits = defineEmits([
  "update:modelValue",
  "switch-order",
  "delete"
]);

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
    default: () => ({
      original_path: null,
      original_size_bytes: null,
      original_name: null,
      current_name: null,
      mime_type: null,
      order: 0,
    }),
  },
  total: {
    type: Number,
    required: true,
  },
  showFileLibrary: {
    type: Boolean,
    default: false,
  },
  isViewOnly: {
    type: Boolean,
    default: false,
  },
});

const fileAttachmentPreview = ref();
const showCropper = ref(false);

const orderOptions = computed(() => {
  if (props.total < 1) return null;
  return Array.from({length: props.total}, (_, i) => ({name: i + 1, value: i}));
});

const updateModelValue = (key, value) => {
  if (props.isViewOnly) return;

  emits("update:modelValue", {
    ...props.modelValue,
    [key]: value,
  });
};

const isCroppableImage = computed(() => {
  return props.modelValue.file && props.modelValue.mime_type?.startsWith("image/");
});

const isNewUpload = computed(() => !!props.modelValue?.file);

const isPreViewable = computed(() => fileAttachmentPreview?.value?.preview?.isExpandable);

const switchOrder = (newIndex) => emits("switch-order", newIndex);

const previewFile = (e) => {
  if (!fileAttachmentPreview.value) return;
  fileAttachmentPreview.value.handlePreview(e);
};

const handleDownload = () => {
  const preferredUrl = props.modelValue.original_path || props.modelValue.high_quality_path || props.modelValue.low_quality_path || props.modelValue.thumbnail_path;

  if (!preferredUrl) {
    toast.error("No file to download");
  }

  const extension = mimeToExtension(props.modelValue.mime_type);

  let filename = props.modelValue.current_name || props.modelValue.original_name;
  filename = filename.toLowerCase().replace(/\.[^/.]+$/, "").replace(/ /g, "-").replace(/[^a-z0-9-]/g, "");
  filename = `${filename}.${extension}`;

  toast.info("File downloading...");

  const a = document.createElement("a");
  a.href = preferredUrl;
  a.download = filename;
  a.target = "_blank";
  a.click();
  a.remove();
};

const handleInputClick = (e) => {
  if (!props.isViewOnly || !isPreViewable.value) return;
  previewFile(e);
};
</script>

<style scoped>
.cursor-pointer-important {
  cursor: pointer !important;
}
</style>
